import { Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { AppConfigResponse } from '../../../models/prompt-library.models';
import { ChatbotService } from 'src/services/chatbot-services/chatbot.service';
import { LoggerService } from 'src/services/logger-services/logger.service';
import { environment } from 'src/environments/environment';
import { RegulationsHubPromptLibraryStore } from '../regulations-hub-prompt-library/regulations-hub-prompt-library.store';

/**
 * Loads RegRadar + app-config questions for the regulations agent hub experience
 * (GET_REGRADAR-style flow) and writes the curated slice to {@link RegulationsHubPromptLibraryStore}.
 * Shared by agent cards and workspace sidebar selection so both paths stay consistent.
 */
@Injectable({
  providedIn: 'root'
})
export class RegulationsHubRegRadarService {
  private readonly regulationsModuleName = 'regulations';

  constructor(
    private readonly chatbotService: ChatbotService,
    private readonly loggerService: LoggerService,
    private readonly regulationsHubPromptLibraryStore: RegulationsHubPromptLibraryStore
  ) {}

  /**
   * Fetches RegRadar data for the regulations module and updates the hub prompt-library store.
   *
   * @param chatbotComponentRef - Optional chatbot instance for user/tenant/domain; otherwise environment.
   */
  loadHubRegRadarSliceForRegulationsAgent(chatbotComponentRef: any | null | undefined): void {
    const userId = this.resolveUserId(chatbotComponentRef);
    const tenantId = this.resolveTenantId(chatbotComponentRef);
    const domainName = this.resolveDomainName(chatbotComponentRef);
    if (!userId || !tenantId || !domainName) {
      this.loggerService.logError(
        `[RegulationsHubRegRadar] RegRadar skipped: missing userId, tenantId, or domainName`,
        'loadHubRegRadarSliceForRegulationsAgent'
      );
      const empty = this.buildRegulationsPromptLibrarySlice({
        app_config_data: [],
        reg_radar_data: []
      });
      this.regulationsHubPromptLibraryStore.setHubRegRadarSlice(empty);
      return;
    }

    this.chatbotService
      .getRegRadarAPI(userId, tenantId, domainName, this.regulationsModuleName)
      .pipe(
        catchError(error => {
          this.loggerService.logError(
            `[RegulationsHubRegRadar] RegRadar API error: ${JSON.stringify(error)}`,
            'loadHubRegRadarSliceForRegulationsAgent'
          );
          return of({ app_config_data: [], reg_radar_data: [] } as AppConfigResponse);
        })
      )
      .subscribe((response: AppConfigResponse) => {
        const normalized: AppConfigResponse = {
          app_config_data: Array.isArray(response?.app_config_data) ? response.app_config_data : [],
          reg_radar_data: Array.isArray(response?.reg_radar_data) ? response.reg_radar_data : []
        };
        const curated = this.buildRegulationsPromptLibrarySlice(normalized);
        this.regulationsHubPromptLibraryStore.setHubRegRadarSlice(curated);
      });
  }

  private resolveUserId(chatbotComponentRef: any | null | undefined): string {
    return (chatbotComponentRef?.UserID != null && chatbotComponentRef?.UserID !== '')
      ? String(chatbotComponentRef.UserID)
      : (environment.LoginUserID ?? '');
  }

  private resolveTenantId(chatbotComponentRef: any | null | undefined): string {
    const fromChatbot =
      chatbotComponentRef?.tenantID ?? chatbotComponentRef?.configdata?.default_tenant_id;
    return (fromChatbot != null && fromChatbot !== '')
      ? String(fromChatbot)
      : (environment.TenantId ?? '');
  }

  private resolveDomainName(chatbotComponentRef: any | null | undefined): string {
    const fromChatbot = chatbotComponentRef?.moduleName;
    return (fromChatbot != null && fromChatbot !== '')
      ? String(fromChatbot)
      : (environment.DomainName ?? '');
  }

  /**
   * Builds up to three prompt rows for the regulations agent:
   * - Both sides have questions: first 2 from reg_radar_data, first 1 from app_config_data.
   * - No app_config questions: first 3 from reg_radar_data only.
   * - No reg_radar questions: first 3 from app_config_data only.
   * - Neither: empty.
   */
  private buildRegulationsPromptLibrarySlice(full: AppConfigResponse): AppConfigResponse {
    const app = full.app_config_data ?? [];
    const reg = full.reg_radar_data ?? [];
    const hasApp = app.length > 0;
    const hasReg = reg.length > 0;

    if (!hasApp && !hasReg) {
      return { app_config_data: [], reg_radar_data: [] };
    }
    if (!hasApp) {
      return { app_config_data: [], reg_radar_data: reg.slice(0, 3) };
    }
    if (!hasReg) {
      return { app_config_data: app.slice(0, 3), reg_radar_data: [] };
    }
    return {
      app_config_data: app.slice(0, 1),
      reg_radar_data: reg.slice(0, 2)
    };
  }
}
